/**
 * Customer-side cutoff helpers — mirror /app/backend/cutoff.py.
 *
 * Each delivery window has:
 *   - `cutoff_time` (HH:MM 24h, e.g. "23:00")
 *   - `cutoff_day_offset` (int, -7..0; default -1) — how many days BEFORE
 *     the delivery date the cutoff falls.
 *
 * `0`  → cutoff is on the delivery day itself
 * `-1` → cutoff is on the day before delivery
 * `-N` → cutoff is N days before delivery
 *
 * The backend re-validates on every POST, so these helpers are purely UX
 * (disable past dates / show readable preview).
 */

import { istDateISO } from "./istDate";

// IST calendar date of `d` — the backend's cutoff calendar (cutoff.py). The
// old toISOString() form returned the UTC date, a day behind 00:00–05:30 IST.
function isoDate(d) { return istDateISO(d); }

function todayISO() { return isoDate(new Date()); }

function addDays(d, days) {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
}

function parseHM(hhmm) {
  if (!hhmm || !hhmm.includes(":")) return { h: 23, m: 59 };
  const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10) || 0);
  return { h, m };
}

/** ISO date (YYYY-MM-DD) of the cutoff moment for delivering on `deliveryDateIso`
 *  via `window`. Returns the date when the cutoff CLOCK time applies. */
function cutoffDateForDelivery(deliveryDateIso, window) {
  const offset = Math.max(-7, Math.min(0, parseInt(window?.cutoff_day_offset ?? -1, 10) || 0));
  // Parse YYYY-MM-DD as a LOCAL date (constructing with `new Date(iso)` parses
  // UTC, which shifts the day by IST). Build it explicitly:
  const [y, m, d] = deliveryDateIso.split("-").map((x) => parseInt(x, 10));
  const base = new Date(y, (m || 1) - 1, d || 1);
  return addDays(base, offset);
}

/** True iff this window is still accepting orders for the given delivery date. */
export function isSlotOpenFor(window, deliveryDateIso) {
  if (!window || window.is_active === false) return false;
  const cutoffDay = cutoffDateForDelivery(deliveryDateIso, window);
  const { h, m } = parseHM(window.cutoff_time);
  const cutoffDt = new Date(cutoffDay);
  cutoffDt.setHours(h, m, 0, 0);
  return new Date() < cutoffDt;
}

/** ISO date of the earliest delivery date that has at least one open active
 *  slot. Walks today → today+14, returns the first hit. */
export function earliestDeliveryDate(windows) {
  const active = (windows || []).filter((w) => w?.is_active !== false);
  if (!active.length) return todayISO();
  for (let i = 0; i < 15; i++) {
    const d = addDays(new Date(), i);
    const iso = isoDate(d);
    if (active.some((w) => isSlotOpenFor(w, iso))) return iso;
  }
  return todayISO();
}

/** True iff the given slot is still orderable for the given delivery date. */
export function isWindowOrderable(window, deliveryDateIso) {
  return isSlotOpenFor(window, deliveryDateIso);
}

export function describeCutoffOffset(offset) {
  const n = parseInt(offset ?? -1, 10) || 0;
  if (n === 0) return "on the day of delivery";
  if (n === -1) return "the day before";
  return `${Math.abs(n)} days before`;
}
